import React, { Fragment, useCallback, useMemo } from 'react';

import CheckboxList from '@zola/zola-ui/src/components/Form/CheckboxList/CheckboxList';
import InfoIcon from '@zola/zola-ui/src/components/SvgIcons/Info';
import Tooltip from '@zola/zola-ui/src/components/Tooltip/Tooltip';

import cx from 'classnames';
import { useForm, useFormState } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';

import { Months, Weekdays } from '~/components/common/form/dates';
import { FormWithMutators } from '~/components/common/form/formWithMutators';

import { formatDate, isWeddingDateInTheFuture } from '../WeddingDateField/dateFunctions';
import { WeddingDateFormFields } from '../WeddingDateField/types';
import { stringifyYear } from './flexibleDateSelectionHelpers';
import { validateFlexibleWeddingDateField } from './validateFlexibleWeddingDateField';

import styles from './flexibleDateSelection.module.less';

export const getMonthIsInFuture = (year: string, month: string) => {
  const nextMonth = parseInt(month, 10) + 1;
  const lastDayOfMonth = formatDate(`${year}-${nextMonth}-0`); // setting the day parameter 0 gets us the last day of the previous month
  return lastDayOfMonth ? isWeddingDateInTheFuture(lastDayOfMonth) : false;
};

export const getMonthOptions = (year: string) => {
  return Months.map((month) => {
    const isFuture = getMonthIsInFuture(year, month.value);
    const key = `${year}${month.label}`;
    return {
      label: month.label.slice(0, 3),
      value: month.value,
      name: key,
      id: key,
      key,
      disabled: !isFuture,
    };
  });
};

export interface FlexibleDateSelectionProps {
  years: string[];
  yearsToShow: string[];
  setYearsToShow: (years: string[]) => void;
  className?: string;
}

const WEEKDAY_OPTIONS = [
  ...Weekdays.map((weekday) => {
    return {
      label: weekday.label.slice(0, 3),
      value: weekday.label,
    };
  }),
];

const SectionLabel: React.FC<{ htmlFor: string }> = ({ htmlFor, children }) => {
  return (
    <label htmlFor={htmlFor} className={styles.sectionLabel}>
      {children}
    </label>
  );
};

const SectionHeader: React.FC<{ className?: string; htmlFor: string; label: React.ReactNode }> = ({
  className,
  htmlFor,
  label,
  children,
}) => {
  return (
    <div className={className}>
      <SectionLabel htmlFor={htmlFor}>{label}</SectionLabel>
      {children}
    </div>
  );
};
const Section: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  children,
  className,
  ...rest
}) => {
  return (
    <div className={cx(styles.section, className)} {...rest}>
      {children}
    </div>
  );
};

const SectionContent: React.FC<{ className?: string }> = ({ children, className }) => {
  return (
    <Fragment>
      <hr />
      <div className={cx(styles.sectionContent, className)}>{children}</div>
    </Fragment>
  );
};

export const FlexibleDateSelection = ({
  years,
  yearsToShow,
  setYearsToShow,
  className,
}: FlexibleDateSelectionProps) => {
  const form = useForm<WeddingDateFormFields>() as FormWithMutators<WeddingDateFormFields>;
  const { values } = useFormState<WeddingDateFormFields>();
  const { setField: setFormField } = form.mutators;

  const { expandedWeddingMonths } = values;

  const updateSelectedYears = useCallback(
    (year: string) => {
      // clear month selections for year when it is unselected
      if (yearsToShow.includes(year)) {
        if (expandedWeddingMonths && expandedWeddingMonths[stringifyYear(year)]) {
          const yearKey = stringifyYear(year);
          const emptyMonth = { [yearKey]: [] };
          const updatedMonths = { ...expandedWeddingMonths, ...emptyMonth };
          setFormField('expandedWeddingMonths', updatedMonths);
        }
        const newYearsToShow = yearsToShow.filter((current) => current !== year);
        setYearsToShow(newYearsToShow);
      } else {
        setYearsToShow([...yearsToShow, year]);
      }
    },
    [setFormField, setYearsToShow, expandedWeddingMonths, yearsToShow]
  );

  // ordered list of selected years, used to display months for each year
  const orderedYears = useMemo(
    () => (yearsToShow ? years.filter((year) => yearsToShow.includes(year)) : []),
    [years, yearsToShow]
  );

  const yearOptions = useMemo(
    () =>
      years.map((year) => {
        const key = stringifyYear(year);

        return {
          label: year,
          value: key,
          name: key,
          onChange: () => updateSelectedYears(year),
        };
      }),
    [updateSelectedYears, years]
  );

  return (
    <fieldset className={cx(styles.flexibleDateSelection, className)}>
      <Section>
        <SectionHeader htmlFor="expandedWeddingYears" label="Year" />
        <SectionContent className={styles.yearSelection}>
          <FieldArray
            name="expandedWeddingYears"
            id="expandedWeddingYears"
            options={yearOptions}
            validate={validateFlexibleWeddingDateField}
          >
            {({ fields, meta }) => (
              <CheckboxList
                inline="horizontal"
                fields={fields}
                useSelectorStyle
                meta={meta}
                options={yearOptions}
                checkboxClassName={styles.yearCheckbox}
              />
            )}
          </FieldArray>
        </SectionContent>
      </Section>

      {orderedYears.map((year) => {
        const options = getMonthOptions(year);
        const key = `expandedWeddingMonths.${stringifyYear(year)}`;
        return (
          <Section key={`${key}wrapper`} data-testid={`months-for-${year}`}>
            <SectionHeader htmlFor={key} label={year} />
            <SectionContent className={styles.monthSelection}>
              <FieldArray
                name={key}
                id={key}
                key={key}
                options={options}
                validate={validateFlexibleWeddingDateField}
              >
                {({ fields, meta }) => (
                  <CheckboxList inline="horizontal" fields={fields} meta={meta} options={options} />
                )}
              </FieldArray>
            </SectionContent>
          </Section>
        );
      })}
      {orderedYears.length > 0 && (
        <Section>
          <SectionHeader
            className={styles.daysHeader}
            htmlFor="expandedWeddingWeekdays"
            label="Days of the week"
          >
            <span className={styles.tooltipWrapper}>
              <InfoIcon width={16} height={16} tabIndex={0} className={styles.tooltipIcon} />
              {/* shown or hidden via CSS, it is better for a11y to always have the tooltip in the DOM */}
              <Tooltip
                className={styles.tooltip}
                text="Weekdays often have more availability and lower pricing"
              />
            </span>
          </SectionHeader>
          <SectionContent className={styles.daySelection}>
            <FieldArray
              name="expandedWeddingWeekdays"
              id="expandedWeddingWeekdays"
              options={WEEKDAY_OPTIONS}
              validate={validateFlexibleWeddingDateField}
            >
              {({ fields, meta }) => (
                <CheckboxList
                  inline="horizontal"
                  fields={fields}
                  meta={meta}
                  options={WEEKDAY_OPTIONS}
                />
              )}
            </FieldArray>
          </SectionContent>
        </Section>
      )}
    </fieldset>
  );
};
